import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell
} from "recharts";
import {
  ArrowLeft, ArrowRight, User, Ruler, Scale, Calendar, MapPin, Utensils,
  Loader2, Info, TrendingUp, Activity, Apple, Flame, Droplets, Dumbbell,
  Leaf, Clock, ChefHat, Sparkles, CheckCircle, CalendarDays, CalendarRange,
  Calendar as CalendarIcon, Download
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format, parseISO } from "date-fns";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";
import PaywallModal from "@/components/PaywallModal";
import DOBPicker from "@/components/DOBPicker";
import { ageFromDOB, toISODate, formatAgeDisplay } from "@/utils/age";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NINNutriGuidePage = () => {
  useTheme();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState("form");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [profile, setProfile] = useState(null);
  const [mealPlanView, setMealPlanView] = useState("day");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [mealPlanScope, setMealPlanScope] = useState("day");
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [dob, setDob] = useState(null);
  const [formData, setFormData] = useState({
    name: "", bone_age: "", present_height: "",
    present_weight: "", expected_height: "", expected_weight: "",
    gender: "", food_type: "", region: ""
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const downloadPDF = async () => {
    setShowPdfModal(false);
    setPdfLoading(true);
    try {
      const response = await api.post(`${API}/nutrition/generate-report`, {
        profile, results, meal_plan_scope: mealPlanScope
      }, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `NutriGuide_Report_${profile?.name || 'Kid'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        try {
          const me = await api.get(`${API}/payments/me`);
          setCreditBalance(me.data.balance || 0);
        } catch {}
        setPaywallOpen(true);
        return;
      }
      let msg = "Failed to generate report";
      if (error.code === "ERR_NETWORK") msg = "Network error — please check your internet connection.";
      else if (error.response?.data) {
        try { const txt = await error.response.data.text(); msg = JSON.parse(txt).detail || msg; } catch {}
      }
      toast.error(msg);
      console.error("PDF error:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!dob) { toast.error("Please pick the child's date of birth"); return false; }
    const ageInfo = ageFromDOB(dob);
    if (!ageInfo || ageInfo.decimalYears <= 0 || ageInfo.decimalYears > 18) {
      toast.error("Date of birth must give an age between 0 and 18 years"); return false;
    }
    const required = ['name', 'bone_age', 'present_height',
      'present_weight', 'expected_height', 'expected_weight', 'gender', 'food_type', 'region'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return false;
      }
    }
    const numericFields = {
      bone_age: { min: 0, max: 18, label: "Bone age" },
      present_height: { min: 30, max: 200, label: "Present height" },
      present_weight: { min: 2, max: 150, label: "Present weight" },
      expected_height: { min: 30, max: 200, label: "Expected height" },
      expected_weight: { min: 2, max: 150, label: "Expected weight" }
    };
    for (const [field, config] of Object.entries(numericFields)) {
      const value = parseFloat(formData[field]);
      if (isNaN(value) || value < config.min || value > config.max) {
        toast.error(`${config.label} must be between ${config.min} and ${config.max}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        bone_age: parseFloat(formData.bone_age),
        chronological_age: ageFromDOB(dob).decimalYears,
        date_of_birth: toISODate(dob),
        present_height: parseFloat(formData.present_height),
        present_weight: parseFloat(formData.present_weight),
        expected_height: parseFloat(formData.expected_height),
        expected_weight: parseFloat(formData.expected_weight)
      };
      const response = await api.post(`${API}/nutrition/analyze`, payload, { timeout: 30000 });
      setResults(response.data);
      setProfile(payload);
      setActiveView("results");
      toast.success("Analysis complete!");
    } catch (error) {
      let msg = "Failed to analyze. Please try again.";
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network")) {
        msg = "Network error — please check your internet connection and try again.";
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      toast.error(msg);
      console.error("Nutrition error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "below_expected": return "text-orange-400";
      case "above_expected": return "text-blue-400";
      default: return "text-green-400";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "below_expected": return "Below Expected";
      case "above_expected": return "Above Expected";
      default: return "On Track";
    }
  };

  const getFoodTypeLabel = (type) => {
    switch (type) {
      case "veg": return "Vegetarian";
      case "non-veg": return "Non-Vegetarian";
      case "mixed": return "Mixed";
      default: return type;
    }
  };

  // ---- FORM VIEW ----
  if (activeView === "form") {
    return (
      <div className="min-h-screen dark bg-mesh">
        <ToolPageNavbar />
        <main className="max-w-3xl mx-auto px-6 pt-28 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-[#FF6B6B] transition-colors mb-6 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to tots360
              </Link>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-3" data-testid="nutriguide-form-title">
                NIN <span className="text-[#FF6B6B]">Nutri</span>Guide
              </h1>
              <p className="text-white/50 text-lg">Enter your child's details for personalized nutrition recommendations</p>
            </div>

            <Card className="bg-white/[0.03] border border-white/10 backdrop-blur-xl rounded-2xl" data-testid="nutrition-form-card">
              <CardHeader>
                <CardTitle className="text-xl text-white font-display">Child Information</CardTitle>
                <CardDescription className="text-white/40">Provide accurate measurements for best results</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2 text-white/70">
                        <User className="w-4 h-4 text-[#FF6B6B]" /> Child's Name
                      </Label>
                      <Input id="name" name="name" placeholder="Enter name" value={formData.name}
                        onChange={handleInputChange} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#FF6B6B]/50" data-testid="input-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70">
                        <User className="w-4 h-4 text-[#FF6B6B]" /> Gender
                      </Label>
                      <Select value={formData.gender} onValueChange={(v) => handleSelectChange("gender", v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a2e] border-white/10">
                          <SelectItem value="male" className="text-white">Male</SelectItem>
                          <SelectItem value="female" className="text-white">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <TooltipProvider>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-white/70">
                          <Calendar className="w-4 h-4 text-[#FF6B6B]" /> Date of Birth
                          <Tooltip><TooltipTrigger><Info className="w-4 h-4 text-white/30" /></TooltipTrigger>
                            <TooltipContent className="bg-[#1a1a2e] border-white/10 text-white"><p>Age is computed from the DOB and shown as X years Y months</p></TooltipContent>
                          </Tooltip>
                        </Label>
                        <DOBPicker value={dob} onChange={setDob} testId="nutri-dob" accent="#FF6B6B" />
                      </div>
                    </TooltipProvider>
                    <TooltipProvider>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-white/70">
                          <Calendar className="w-4 h-4 text-[#FF6B6B]" /> Bone Age (years)
                          <Tooltip><TooltipTrigger><Info className="w-4 h-4 text-white/30" /></TooltipTrigger>
                            <TooltipContent className="bg-[#1a1a2e] border-white/10 text-white"><p>Skeletal maturity age from X-ray</p></TooltipContent>
                          </Tooltip>
                        </Label>
                        <Input name="bone_age" type="number" step="0.5" min="0" max="18" placeholder="e.g., 8"
                          value={formData.bone_age} onChange={handleInputChange}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="input-bone-age" />
                      </div>
                    </TooltipProvider>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><Ruler className="w-4 h-4 text-[#FF6B6B]" /> Present Height (cm)</Label>
                      <Input name="present_height" type="number" step="0.1" min="30" max="200" placeholder="e.g., 125"
                        value={formData.present_height} onChange={handleInputChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="input-present-height" />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><Ruler className="w-4 h-4 text-[#FF6B6B]" /> Expected Height (cm)</Label>
                      <Input name="expected_height" type="number" step="0.1" min="30" max="200" placeholder="e.g., 130"
                        value={formData.expected_height} onChange={handleInputChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="input-expected-height" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><Scale className="w-4 h-4 text-[#FF6B6B]" /> Present Weight (kg)</Label>
                      <Input name="present_weight" type="number" step="0.1" min="2" max="150" placeholder="e.g., 25"
                        value={formData.present_weight} onChange={handleInputChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="input-present-weight" />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><Scale className="w-4 h-4 text-[#FF6B6B]" /> Expected Weight (kg)</Label>
                      <Input name="expected_weight" type="number" step="0.1" min="2" max="150" placeholder="e.g., 28"
                        value={formData.expected_weight} onChange={handleInputChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="input-expected-weight" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><Utensils className="w-4 h-4 text-[#FF6B6B]" /> Food Preference</Label>
                      <Select value={formData.food_type} onValueChange={(v) => handleSelectChange("food_type", v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-food-type">
                          <SelectValue placeholder="Select food type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a2e] border-white/10">
                          <SelectItem value="veg" className="text-white">Vegetarian</SelectItem>
                          <SelectItem value="non-veg" className="text-white">Non-Vegetarian</SelectItem>
                          <SelectItem value="mixed" className="text-white">Mixed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-white/70"><MapPin className="w-4 h-4 text-[#FF6B6B]" /> Region</Label>
                      <Select value={formData.region} onValueChange={(v) => handleSelectChange("region", v)}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-region">
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a2e] border-white/10">
                          {["South India", "North India", "East India", "West India", "North East India", "Central India"].map(r => (
                            <SelectItem key={r} value={r} className="text-white">{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-[#FF6B6B] hover:bg-[#e05555] text-white text-lg py-6 rounded-xl font-semibold"
                    disabled={loading} data-testid="submit-analysis-btn">
                    {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>) : "Get Nutrition Analysis"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-6 p-4 bg-[#FF6B6B]/10 rounded-xl border border-[#FF6B6B]/30">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-[#FF6B6B] shrink-0 mt-0.5" />
                <div className="text-sm text-white/60">
                  <p className="font-medium mb-1 text-[#FF6B6B]">About Expected Values</p>
                  <p>Expected height and weight values should be based on NIN growth charts for your child's age and gender.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </main>
        <Footer />
        <Toaster position="top-right" />
      </div>
    );
  }

  // ---- RESULTS VIEW ----
  if (!results || !profile) return null;

  const { daily_requirements, growth_status, regional_food_materials, meal_plans, weekly_meal_plans, monthly_meal_plans, recommendations, ai_insights } = results;

  const growthData = [
    { name: "Height (cm)", actual: profile.present_height, expected: profile.expected_height },
    { name: "Weight (kg)", actual: profile.present_weight, expected: profile.expected_weight }
  ];
  const macroData = [
    { name: "Protein", value: daily_requirements.protein, color: "#FF6B6B" },
    { name: "Carbs", value: daily_requirements.carbs, color: "#4ECDC4" },
    { name: "Fats", value: daily_requirements.fats, color: "#A855F7" },
    { name: "Fiber", value: daily_requirements.fiber, color: "#FFE66D" }
  ];

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="max-w-6xl mx-auto px-6 pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <button onClick={() => { setActiveView("form"); setResults(null); }}
                className="flex items-center gap-2 text-white/40 hover:text-[#FF6B6B] transition-colors mb-3 text-sm" data-testid="new-analysis-btn">
                <ArrowLeft className="w-4 h-4" /> New Analysis
              </button>
              <h1 className="font-display text-3xl md:text-4xl text-white mb-1" data-testid="results-title">
                Nutrition Analysis for {profile.name}
              </h1>
              <p className="text-white/40">
                {dob ? formatAgeDisplay(dob) : `${profile.chronological_age} years`} | {profile.gender === "male" ? "Boy" : "Girl"} | {profile.region} | {getFoodTypeLabel(profile.food_type)}
              </p>
            </div>
            <Button onClick={() => setShowPdfModal(true)} disabled={pdfLoading}
              className="bg-[#FF6B6B] hover:bg-[#e05555] text-white font-semibold px-6" data-testid="nutrition-download-pdf-btn">
              {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download PDF Report
            </Button>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="quick-stats">
          {[
            { icon: Flame, label: "kcal/day", value: daily_requirements.calories, color: "#FF6B6B" },
            { icon: Dumbbell, label: "Protein", value: `${daily_requirements.protein}g`, color: "#FFE66D" },
            { icon: Activity, label: "BMI", value: growth_status.bmi, color: "#4ECDC4" },
            { icon: TrendingUp, label: "Bone Age Diff", value: `${growth_status.bone_age_difference > 0 ? "+" : ""}${growth_status.bone_age_difference}`, color: "#A855F7" }
          ].map((stat, i) => (
            <Card key={i} className="bg-white/[0.03] border border-white/10 backdrop-blur-xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${stat.color}20` }}>
                    <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-white/40">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Results Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white/[0.03] p-1 rounded-xl border border-white/10 flex-wrap">
            {[
              { value: "overview", label: "Overview" },
              { value: "nutrition", label: "Nutrition" },
              { value: "foods", label: "Regional Foods" },
              { value: "meals", label: "Meal Plans" },
            ].map(tab => (
              <TabsTrigger key={tab.value} value={tab.value}
                className="rounded-lg data-[state=active]:bg-[#FF6B6B] data-[state=active]:text-white text-white/50"
                data-testid={`tab-${tab.value}`}>{tab.label}</TabsTrigger>
            ))}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white/[0.03] border border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white font-display">
                    <TrendingUp className="w-5 h-5 text-[#FF6B6B]" /> Growth Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="growth-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={growthData} barGap={8}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                        <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} />
                        <RechartsTooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                        <Legend />
                        <Bar dataKey="actual" name="Actual" fill="#FF6B6B" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="expected" name="Expected" fill="rgba(255,255,255,0.15)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white/[0.03] border border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white font-display">
                    <Activity className="w-5 h-5 text-[#FF6B6B]" /> Growth Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {[
                    { label: "Height Progress", status: growth_status.height_status, pct: growth_status.height_percentile, gap: `${growth_status.height_gap} cm` },
                    { label: "Weight Progress", status: growth_status.weight_status, pct: growth_status.weight_percentile, gap: `${growth_status.weight_gap} kg` }
                  ].map((item, i) => (
                    <div key={i} data-testid={`${item.label.toLowerCase().replace(' ', '-')}-status`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-white/60">{item.label}</span>
                        <span className={`text-sm font-semibold ${getStatusColor(item.status)}`}>{getStatusLabel(item.status)}</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#FF6B6B] to-[#FF6B6B]/70 rounded-full transition-all duration-500" style={{ width: `${Math.min(item.pct, 100)}%` }} />
                      </div>
                      <p className="text-xs text-white/30 mt-1">{item.pct}% of expected | Gap: {item.gap}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            <Card className="bg-white/[0.03] border border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white font-display">
                  <CheckCircle className="w-5 h-5 text-[#4ECDC4]" /> Key Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recommendations-list">
                  {recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-xl border border-white/[0.06]">
                      <div className="w-6 h-6 bg-[#4ECDC4]/20 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-semibold text-[#4ECDC4]">{i + 1}</span>
                      </div>
                      <p className="text-sm text-white/60">{rec}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Nutrition Tab */}
          <TabsContent value="nutrition" className="space-y-6">
            <Card className="bg-white/[0.03] border border-white/10">
              <CardHeader><CardTitle className="flex items-center gap-2 text-white font-display"><Apple className="w-5 h-5 text-[#FF6B6B]" /> Daily Macronutrients</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="h-64 w-full lg:w-1/2" data-testid="macros-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={macroData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value"
                          label={({ name, value }) => `${name}: ${value}g`}>
                          {macroData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                        </Pie>
                        <RechartsTooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4">
                    {macroData.map(item => (
                      <div key={item.name} className="flex items-center gap-2 bg-white/[0.03] px-4 py-2 rounded-lg border border-white/[0.06]">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-white/60">{item.name}: <strong className="text-white">{item.value}g</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vitamins & Minerals with bar charts */}
            {[
              { title: "Water Soluble Vitamins", icon: Droplets, data: daily_requirements.water_soluble_vitamins, color: "#4ECDC4", barColor: "#0891b2" },
              { title: "Fat Soluble Vitamins", icon: Leaf, data: daily_requirements.fat_soluble_vitamins, color: "#FFE66D", barColor: "#ca8a04" },
              { title: "Minerals", icon: Dumbbell, data: daily_requirements.minerals, color: "#4ECDC4", barColor: "#2563eb" },
              { title: "Trace Elements", icon: Activity, data: daily_requirements.trace_elements, color: "#A855F7", barColor: "#7c3aed" },
              { title: "Antioxidants", icon: Sparkles, data: daily_requirements.antioxidants, color: "#10B981", barColor: "#059669" },
              { title: "Essential Fatty Acids", icon: Sparkles, data: daily_requirements.essential_fatty_acids, color: "#F472B6", barColor: "#db2777" },
            ].map((section, si) => {
              if (!section.data || Object.keys(section.data).length === 0) return null;
              const chartData = section.data ? Object.entries(section.data).map(([key, value]) => ({
                name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).substring(0, 16),
                amount: value.amount,
                unit: value.unit
              })) : [];
              return (
              <Card key={si} className="bg-white/[0.03] border border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white font-display">
                    <section.icon className="w-5 h-5" style={{ color: section.color }} /> {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.length > 0 && (
                    <div className="h-64 mb-4" data-testid={`chart-${section.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 40, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                          <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                          <YAxis dataKey="name" type="category" width={110} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 9 }} />
                          <RechartsTooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                            formatter={(value, name, props) => [`${value} ${props.payload.unit}`, 'Amount']} />
                          <Bar dataKey="amount" fill={section.barColor} radius={[0, 6, 6, 0]} barSize={16} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {section.data && Object.entries(section.data).map(([key, value]) => (
                      <div key={key} className="p-3 rounded-xl border" style={{ background: `${section.color}08`, borderColor: `${section.color}20` }}>
                        <p className="text-xs uppercase tracking-wide truncate" style={{ color: section.color }}>{key.replace(/_/g, ' ')}</p>
                        <p className="text-lg font-semibold text-white">{value.amount} {value.unit}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              );
            })}

            {/* Water Intake */}
            {daily_requirements.water?.daily_intake && (
              <Card className="bg-white/[0.03] border border-white/10">
                <CardContent className="flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full bg-sky-400/10 border-4 border-sky-400/50 flex items-center justify-center mb-4 mx-auto">
                      <div>
                        <p className="text-4xl font-bold text-sky-400">{daily_requirements.water.daily_intake.amount}</p>
                        <p className="text-sm text-sky-300">{daily_requirements.water.daily_intake.unit}/day</p>
                      </div>
                    </div>
                    <p className="text-white/40">Recommended daily water intake</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-center">
              <div className="bg-[#FF6B6B]/10 rounded-xl px-6 py-3 border border-[#FF6B6B]/30">
                <p className="text-center text-sm text-[#FF6B6B]">All values based on <span className="font-semibold">ICMR-NIN 2020 RDA Guidelines</span></p>
              </div>
            </div>
          </TabsContent>

          {/* Regional Foods Tab */}
          <TabsContent value="foods" className="space-y-6">
            <Card className="bg-white/[0.03] border border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white font-display">
                  <Apple className="w-5 h-5 text-[#FF6B6B]" /> Regional Food Materials from {profile.region}
                </CardTitle>
                <CardDescription className="text-white/40">Raw ingredients rich in required nutrients (ICMR-NIN Food Codes)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6" data-testid="regional-foods-list">
                  {["Fruit", "Vegetable", "Leafy Green", "Millet", "Grain", "Pulse", "Dairy", "Protein", "Nut", "Seed", "Herb", "Spice", "Oil"].map(cat => {
                    const items = regional_food_materials.filter(f => f.category === cat);
                    if (!items.length) return null;
                    const catColors = {
                      "Fruit": "#FF6B6B", "Vegetable": "#4ECDC4", "Leafy Green": "#4ECDC4", "Millet": "#FFE66D",
                      "Grain": "#FFE66D", "Pulse": "#FF6B6B", "Dairy": "#4ECDC4", "Protein": "#A855F7",
                      "Nut": "#FFE66D", "Seed": "#FFE66D", "Herb": "#4ECDC4", "Spice": "#FF6B6B", "Oil": "#FFE66D"
                    };
                    const color = catColors[cat] || "#4ECDC4";
                    return (
                      <div key={cat} className="space-y-3">
                        <h3 className="text-lg font-semibold text-white">{cat === "Leafy Green" ? "Leafy Greens" : cat + "s"}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items.map((food, i) => (
                            <div key={i} className="p-4 rounded-xl border" style={{ background: `${color}08`, borderColor: `${color}20` }}>
                              <div className="flex justify-between items-start mb-2">
                                <h4 className="font-semibold text-white">{food.name}</h4>
                                {food.food_code && (
                                  <span className="px-2 py-0.5 rounded text-xs font-mono border" style={{ color, borderColor: `${color}40`, background: `${color}10` }}>
                                    {food.food_code}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm text-[#4ECDC4]"><Leaf className="w-3 h-3 inline mr-1" />{food.nutrition_benefits}</p>
                                <p className="text-sm text-[#FFE66D]"><Utensils className="w-3 h-3 inline mr-1" />Daily: {food.quantity_per_day}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meal Plans Tab */}
          <TabsContent value="meals" className="space-y-6">
            <Card className="bg-white/[0.03] border border-white/10">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-white font-display">
                      <ChefHat className="w-5 h-5 text-[#FF6B6B]" /> Meal Plans Calendar
                    </CardTitle>
                    <CardDescription className="text-white/40">Day, Week & Month views</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { key: "day", icon: CalendarIcon, label: "Day" },
                      { key: "week", icon: CalendarRange, label: "Week" },
                      { key: "month", icon: CalendarDays, label: "Month" },
                    ].map(v => (
                      <Button key={v.key} onClick={() => setMealPlanView(v.key)} size="sm"
                        className={mealPlanView === v.key ? "bg-[#FF6B6B] text-white hover:bg-[#e05555]" : "bg-white/5 text-white/50 hover:bg-white/10 border border-white/10"}>
                        <v.icon className="w-4 h-4 mr-1" /> {v.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {mealPlanView === "day" && (
                  <div className="space-y-4" data-testid="meal-plans-day">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Today's Meal Plan</h3>
                      <div className="text-sm text-white/30">{format(new Date(), "EEEE, MMMM d, yyyy")}</div>
                    </div>
                    {meal_plans.map((meal, i) => (
                      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                        className="p-4 bg-white/[0.03] rounded-xl border border-white/[0.06]">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Clock className="w-4 h-4 text-[#FF6B6B]" />
                              <span className="text-sm font-medium text-[#FF6B6B]">{meal.meal_type}</span>
                            </div>
                            <h4 className="font-semibold text-white">{meal.name}</h4>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-[#FF6B6B]">{meal.nutrition_info.calories}</span>
                            <span className="text-xs text-white/30 ml-1">kcal</span>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex flex-wrap gap-1">
                            {meal.ingredients.map((ing, j) => (
                              <span key={j} className="text-xs bg-white/5 text-white/60 px-2 py-1 rounded-full border border-white/[0.06]">{ing}</span>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-white/40">{meal.recipe}</p>
                        <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-4 text-xs text-white/30">
                          <span><strong className="text-[#FFE66D]">{meal.nutrition_info.protein}g</strong> protein</span>
                          {meal.nutrition_info.key_nutrients && <span>Key: {meal.nutrition_info.key_nutrients}</span>}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {mealPlanView === "week" && weekly_meal_plans && (
                  <div className="space-y-4" data-testid="meal-plans-week">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {weekly_meal_plans.map((dayPlan, i) => (
                        <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                          className="p-4 bg-white/[0.03] rounded-xl border border-white/[0.06] hover:border-[#FF6B6B]/40 transition-all">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-[#FF6B6B] font-semibold">{dayPlan.day_name}</p>
                              <p className="text-xs text-white/30">{format(parseISO(dayPlan.date), "MMM d")}</p>
                            </div>
                            <div className="w-10 h-10 bg-[#FF6B6B]/20 rounded-full flex items-center justify-center">
                              <span className="text-[#FF6B6B] font-bold">{dayPlan.day_number}</span>
                            </div>
                          </div>
                          <div className="space-y-2 mb-3">
                            {dayPlan.meals.slice(0, 3).map((meal, mi) => (
                              <div key={mi} className="flex items-center gap-2 text-xs">
                                <span className="w-2 h-2 bg-[#FF6B6B] rounded-full" />
                                <span className="text-white/50 truncate">{meal.name}</span>
                              </div>
                            ))}
                            {dayPlan.meals.length > 3 && <p className="text-xs text-white/20">+{dayPlan.meals.length - 3} more</p>}
                          </div>
                          <div className="pt-3 border-t border-white/[0.06] flex justify-between text-xs">
                            <span className="text-white/30"><strong className="text-[#FF6B6B]">{dayPlan.total_calories}</strong> kcal</span>
                            <span className="text-white/30"><strong className="text-[#4ECDC4]">{dayPlan.total_protein}g</strong> protein</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {mealPlanView === "month" && monthly_meal_plans && (
                  <div className="space-y-4" data-testid="meal-plans-month">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
                      {monthly_meal_plans.map((dayPlan, i) => (
                        <div key={i} className="p-2 bg-white/[0.02] rounded-lg border border-white/[0.04] text-center hover:border-[#FF6B6B]/30 transition-all cursor-default">
                          <p className="text-[10px] text-white/30">{dayPlan.day_name.substring(0, 3)}</p>
                          <p className="text-lg font-bold text-white">{dayPlan.day_number}</p>
                          <p className="text-[10px] text-[#FF6B6B]">{dayPlan.total_calories} kcal</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
      <Toaster position="top-right" />
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} balance={creditBalance} />

      {/* PDF Meal-Plan Scope Modal */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="bg-[#0F1C3F] border border-white/10 text-white max-w-md" data-testid="pdf-mealplan-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Customise your PDF report</DialogTitle>
            <DialogDescription className="text-white/60">
              Choose how much of the meal plan to include. Daily nutrition, vitamins, minerals and antioxidants are always included.
            </DialogDescription>
          </DialogHeader>

          <RadioGroup value={mealPlanScope} onValueChange={setMealPlanScope} className="space-y-2 my-4">
            {[
              { v: "day",   label: "Today's meal plan",       hint: "5 meals — concise 1-page summary" },
              { v: "week",  label: "Weekly meal plan",        hint: "7 days × 5 meals — adds ~3-4 pages" },
              { v: "month", label: "Monthly meal plan",       hint: "30 days × 5 meals — adds ~12-15 pages" },
              { v: "none",  label: "Skip meal plan",          hint: "Nutrition + recommendations only" },
            ].map((opt) => (
              <label key={opt.v}
                htmlFor={`mp-${opt.v}`}
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all
                  ${mealPlanScope === opt.v ? "border-[#FF6B6B] bg-[#FF6B6B]/[0.08]" : "border-white/10 hover:border-white/30"}`}
                data-testid={`pdf-scope-option-${opt.v}`}>
                <RadioGroupItem value={opt.v} id={`mp-${opt.v}`} className="mt-0.5 border-white/40" />
                <div className="flex-1">
                  <p className="text-white font-medium text-sm">{opt.label}</p>
                  <p className="text-white/40 text-xs mt-0.5">{opt.hint}</p>
                </div>
              </label>
            ))}
          </RadioGroup>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setShowPdfModal(false)}
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="pdf-modal-cancel-btn">
              Cancel
            </Button>
            <Button onClick={downloadPDF} disabled={pdfLoading}
              className="bg-[#FF6B6B] hover:bg-[#e05555] text-white font-semibold"
              data-testid="pdf-modal-confirm-btn">
              {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NINNutriGuidePage;
